import React, { useEffect, useRef, useState } from "react";
import {
  deleteStrategy,
  PhoenixContext,
  PhoenixContextSummary,
  uploadStrategyFiles,
} from "../api";

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_PDF_UPLOAD_MB = 20;
const MAX_FILES = 6;

// ─── Props ────────────────────────────────────────────────────────────────────

interface StrategyUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUploaded?: (summary: PhoenixContextSummary) => void;
  onDeleted?: () => void;
  existingContext?: PhoenixContext | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSizeMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function isOversized(file: File): boolean {
  return file.size > MAX_PDF_UPLOAD_MB * 1024 * 1024;
}

function hasAnyOversized(files: File[]): boolean {
  return files.some(isOversized);
}

function formatUploadedAt(isoString: string): string {
  if (!isoString) return "";
  return isoString.slice(0, 10);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const StrategyUploadDialog: React.FC<StrategyUploadDialogProps> = ({
  open,
  onClose,
  onUploaded,
  onDeleted,
  existingContext,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<PhoenixContextSummary | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setDragActive(false);
      setUploading(false);
      setProgress(0);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  // Esc key closes dialog
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    setFiles((prev) => [...prev, ...dropped].slice(0, MAX_FILES));
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selected].slice(0, MAX_FILES));
    e.target.value = "";
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleUpload() {
    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const summary = await uploadStrategyFiles(files, setProgress);
      setSuccess(summary);
      onUploaded?.(summary);
    } catch (e: any) {
      const status = e.response?.status;
      if (status === 413) {
        setError("Soubor je příliš velký (max 20 MB).");
      } else if (status === 415) {
        setError("Nepodporovaný formát — pouze PDF.");
      } else if (status === 422) {
        const detail = e.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Soubor se nepodařilo zpracovat.");
      } else if (status === 401) {
        setError("Přihlášení vypršelo. Obnovte stránku.");
      } else {
        setError("Nahrávání selhalo. Zkuste znovu.");
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function handleDelete() {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Opravdu smazat aktuální Phoenix kontext?")) return;
    setUploading(true);
    setError(null);
    try {
      await deleteStrategy();
      onDeleted?.();
      onClose();
    } catch {
      setError("Smazání selhalo.");
    } finally {
      setUploading(false);
    }
  }

  const uploadDisabled =
    files.length === 0 || hasAnyOversized(files) || files.length > MAX_FILES || uploading;

  if (!open) return null;

  return (
    /* Overlay */
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "5rem 1rem 1rem",
        overflowY: "auto",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-modal="true"
    >
      {/* Modal box */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="strategy-dialog-title"
        style={{
          background: "rgba(9,9,11,0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "1rem",
          padding: "1.5rem",
          maxWidth: "32rem",
          width: "100%",
          color: "#f4f4f5",
          fontFamily: "'Inter', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <div style={{ marginBottom: "1.25rem" }}>
          <h2
            id="strategy-dialog-title"
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: "1.125rem",
              color: "#fafafa",
              margin: 0,
              marginBottom: "0.375rem",
            }}
          >
            Phoenix Electoral Analytics
          </h2>
          <p
            style={{
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "rgba(255,255,255,0.5)",
              margin: 0,
            }}
          >
            Nahrajte výstupy ze strategie a analýz pro personalizované briefy.
          </p>
        </div>

        {/* Existing context display */}
        {existingContext && !uploading && !success && (
          <div
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "0.75rem",
              padding: "0.875rem 1rem",
              marginBottom: "1rem",
              fontSize: "0.8125rem",
            }}
          >
            <p
              style={{
                color: "rgba(255,255,255,0.7)",
                margin: "0 0 0.375rem",
                fontWeight: 400,
              }}
            >
              Aktuální kontext: {existingContext.file_count} souborů, nahráno{" "}
              {formatUploadedAt(existingContext.uploaded_at)}
            </p>
            {Object.keys(existingContext.sections).length > 0 && (
              <ul
                style={{
                  margin: "0 0 0.75rem",
                  padding: "0 0 0 1rem",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.75rem",
                  lineHeight: 1.6,
                }}
              >
                {Object.keys(existingContext.sections).map((sec) => (
                  <li key={sec}>{sec}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={handleDelete}
              disabled={uploading}
              style={{
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: "#fca5a5",
                borderRadius: "0.5rem",
                padding: "0.375rem 0.875rem",
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              Smazat kontext
            </button>
          </div>
        )}

        {/* Drag-and-drop zone — shown when no files selected */}
        {files.length === 0 && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragActive ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.2)"}`,
              background: dragActive ? "rgba(255,255,255,0.05)" : "transparent",
              borderRadius: "0.75rem",
              padding: "2rem",
              textAlign: "center",
              transition: "all 0.15s ease",
              marginBottom: "1rem",
              cursor: "default",
            }}
          >
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.875rem",
                margin: "0 0 0.75rem",
              }}
            >
              Přetáhněte PDF sem nebo
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#fafafa",
                borderRadius: "0.5rem",
                padding: "0.5rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              vybrat soubory
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              aria-label="Vybrat PDF soubory"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />
          </div>
        )}

        {/* File list — shown when files are selected */}
        {files.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              aria-label="Vybrat PDF soubory"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              {files.map((file, idx) => {
                const oversized = isOversized(file);
                return (
                  <li
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: oversized
                        ? "rgba(239,68,68,0.06)"
                        : "rgba(255,255,255,0.04)",
                      border: `1px solid ${oversized ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.08)"}`,
                      borderRadius: "0.5rem",
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          color: oversized ? "#fca5a5" : "rgba(255,255,255,0.8)",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </span>
                      <span
                        style={{
                          color: oversized ? "#f87171" : "rgba(255,255,255,0.35)",
                          fontSize: "0.75rem",
                        }}
                      >
                        {formatFileSizeMB(file.size)}
                        {oversized && " — příliš velký (max 20 MB)"}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      aria-label={`Odebrat ${file.name}`}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.35)",
                        padding: "0.25rem",
                        marginLeft: "0.5rem",
                        borderRadius: "4px",
                        flexShrink: 0,
                      }}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
            {files.length > MAX_FILES && (
              <p
                style={{
                  color: "#fca5a5",
                  fontSize: "0.75rem",
                  marginTop: "0.5rem",
                }}
              >
                Maximálně {MAX_FILES} souborů najednou.
              </p>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                marginTop: "0.625rem",
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.75rem",
                cursor: "pointer",
                padding: "0",
                textDecoration: "underline",
              }}
            >
              Přidat další soubory
            </button>
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginBottom: "1rem" }}>
            <div
              style={{
                height: "4px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: progress + "%",
                  height: "100%",
                  background: "rgba(255,255,255,0.6)",
                  transition: "width 0.2s ease",
                }}
              />
            </div>
            <p
              style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.75rem",
                marginTop: "0.375rem",
              }}
            >
              Nahrávání... {progress} %
            </p>
          </div>
        )}

        {/* Error panel */}
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(127,29,29,0.5)",
              border: "1px solid rgba(239,68,68,0.3)",
              color: "#fecaca",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.8125rem",
              marginBottom: "1rem",
            }}
          >
            {error}
          </div>
        )}

        {/* Success panel */}
        {success && (
          <div
            role="status"
            style={{
              background: "rgba(6,78,59,0.5)",
              border: "1px solid rgba(52,211,153,0.3)",
              color: "#d1fae5",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.8125rem",
              marginBottom: "1rem",
            }}
          >
            Hotovo. {success.file_count} souborů, sekcí:{" "}
            {success.sections_found.join(", ") || "—"}
          </div>
        )}

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
            marginTop: "0.5rem",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.6)",
              borderRadius: "0.5rem",
              padding: "0.5rem 1.25rem",
              fontSize: "0.875rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploadDisabled}
            style={{
              background: uploadDisabled ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)",
              border: "none",
              color: uploadDisabled ? "rgba(255,255,255,0.3)" : "#050505",
              borderRadius: "0.5rem",
              padding: "0.5rem 1.5rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: uploadDisabled ? "not-allowed" : "pointer",
              transition: "all 0.15s ease",
            }}
          >
            Nahrát
          </button>
        </div>
      </div>
    </div>
  );
};
